export enum MapServerType {
    ARCGIS = "ARCGIS",
    CESIUM = "CESIUM",
    GOOGLE = "GOOGLE",
    BING = "BING",
    OSM = "OSM",
    TMS = "TMS",
}

export enum RESULT_STATUS {
    SUCCESS = 1,
    FAILED = 0,
}

export enum GEO_TYPE {
    POINT = "point",
    POLYLINE = "polyline",
    POLYGON = "polygon",
    CIRCLE = "circle",
    RECTANGLE = "rectangle",
}

export interface IResult {
    status: RESULT_STATUS;
    message?: string;
    data?: Object;
}

export interface IPointGeometry {
    x: number;
    y: number;
    z?: number;
}

export interface IPolylineGeometry {
    paths: IPointGeometry[];
}

export interface IPolygonGeometry {
    rings: IPointGeometry[];
    height?: number; //多边形高度，default 5
    holes?: IPolygonGeometry[]; //暂不用
}

export interface IPointSymbol {
    //图元边框
    outline?: {
        width?: number;
        color?: number[]; //颜色 [r,g,b] | [r,g,b,a]
    };
    color?: number[]; //图元颜色
    size?: number; //图元大小

    url?: string; //图片资源地址 (若为动图则为图片资源文件夹)
    vAnchor?: "center" | "top" | "bottom" | "baseline"; //图片垂直锚点
    hAnchor?: "center" | "left" | "right"; //图片水平锚点
    rotation?: number; //旋转角度
    width?: number; //图片宽度
    height?: number; //图片高度
    imgBgSize?: number[]; //图片背景大小@notice 暂时不搞这个
    xoffset?: number; //图标x偏移
    yoffset?: number; //图标y偏移

    //动图参数
    animatedPicInterval?: number; //图片间隔毫秒数
    animatedPicNums?: number; //图片数量
}

export interface IPolylineSymbol {
    color?: number[]; //线颜色
    width?: number; //线宽
    style?: "solid" | "dashed"; //线样式
    nodeSymbol?: IPointSymbol; //节点样式 @notice暂未实现
}

export interface IPolygonSymbol {
    outline: IPolylineSymbol; //边线颜色
    color?: number[]; //填充颜色 [r,g,b] | [r,g,b,a]
}

export interface IGesture {
    //姿态参数，均为角度制
    heading: number; //偏航角
    pitch: number; //俯仰角
    roll: number; //滚动角
}

export interface IPerspective {
    //视角参数，均为角度制
    heading: number; //偏航角
    pitch: number; //俯仰角
    range: number; //视线距离
}

export const DefaultPoint: IPointSymbol = {
    outline: {
        width: 1,
        color: [255, 0, 255, 1],
    },
    color: [0, 255, 0, 1],
    size: 10,
};

export const DefaultPolyline: IPolylineSymbol = {
    color: [0, 255, 0, 1],
    width: 1,
    style: "solid",
};

export const DefaultPolygon: IPolygonSymbol = {
    outline: DefaultPolyline,
    color: [255, 255, 0, 0.5],
};

export interface ICenterLevelParams {
    center?: number[]; //中心点坐标
    level?: number; //地图显示层级
}

export interface IDrawOverlaysAddParams {
    drawType: GEO_TYPE; // 绘制类型 "point" | "polyline" | "polygon" | "rectangle" | "circle"
    id?: string; //标识id
    type?: string; //标识type
    symbol?: IPointSymbol | IPolylineSymbol | IPolygonSymbol; //overlay的样式符号
    callback?: any; //回调函数，传入绘制结果对象
}

export interface IDrawOverlaysDeleteParams {
    ids?: string[]; //标识id
    types?: string[]; //标识type
}

export interface IOverlay {
    geotype: GEO_TYPE; // 绘制类型 "point" | "polyline" | "polygon" | "rectangle" | "circle"
    geometry: IPointGeometry | IPolylineGeometry | IPolygonGeometry; //几何体坐标
    id?: string; //标识id
    type?: string; //标识type
    symbol?: IPointSymbol | IPolylineSymbol | IPolygonSymbol; //overlay的样式符号
    attributes?: object; //自定义的属性字段
}

export interface IOverlaysAddParams {
    overlays: IOverlay[];
    type?: string; //覆盖物标识type
    symbol?: IPointSymbol | IPolylineSymbol | IPolygonSymbol; //overlay的样式符号
    callback?: any; //回调函数，传入绘制结果对象
    viewToResults?: boolean; //是否显示撒点到地图中心
}

export interface IOverlaysDeleteParams {
    ids?: string[]; //标识id
    types?: string[]; //标识type
}

export interface IDriveModelParams {
    id?: string; //model的自定义唯一id，有默认值
    type?: string; //Model 的类型
    url: string; //model资源路径
    position: IPointGeometry; //模型初始位置
    gesture?: IGesture; //模型初始姿态
    zoomToResult?: boolean; //是否显示加载结果，默认false
    attributes?: object; //自定义属性
}

export interface IDriveRouteParams {
    id: string; //模型id
    type: string; //模型的类型
    route: Array<IPointGeometry>; //路线经纬度坐标
    timeCost: Array<number>; //用时，单位秒。数组长度应为route的长度-1
    loop: boolean; //是否循环
}

export interface IDriveStatusParams {
    id: string; //模型id
    type: string; //模型的类型
    gesture?: IGesture; //模型姿态
    position?: IPointGeometry; //模型位置
    speed?: number; //速度，单位m/s
}

export interface IDriveModelDeleteParams {
    ids?: string[];
    types?: string[];
}

export interface IDriveFocusParams {
    id: string;
    type: string;
    perspective?: IPerspective; //聚焦模型的视角参数，提供可自定义详细视角
}

export interface IModelParams {
    id: string;
    type: string;
    url: string;
    position: IPointGeometry; //模型初始位置
    gesture?: IGesture; //模型初始姿态
    zoomToResult?: boolean; //是否显示加载结果，默认false
}

export interface IModelRemoveParams {
    id?: string;
    type: string;
}

export interface IMapApp {
    //设置地图中心和显示层级
    setMapCenterAndLevel: (params: ICenterLevelParams) => void;
    //绘制覆盖物
    startDrawOverlays: (params: IDrawOverlaysAddParams) => Promise<IResult>;
    // stopDrawOverlays: () => Promise<IResult>;
    deleteDrawOverlays: (params: IDrawOverlaysDeleteParams) => Promise<IResult>;
    //地图覆盖物
    addOverlays?: (params: IOverlaysAddParams) => Promise<IResult>;
    deleteOverlays?: (params: IOverlaysDeleteParams) => Promise<IResult>;
    //实时驾驶
    // loadDriveModel: (params: IDriveModelParams) => Promise<IResult>;
    // changeDriveStatus: (params: IDriveStatusParams) => void;
    // removeDriveModel: (params: IDriveModelDeleteParams) => IResult;
    // focusDriveModel: (params: IDriveFocusParams) => void;
    // cancelFocusDriveModel: () => void;
    // //按既定路线行驶
    // loadRouteDriveModel: (p: IDriveModelParams) => Promise<IResult>;
    // removeRouteDriveModel: (p: IModelRemoveParams) => void;
    // focusRouteDrive: (p: IDriveFocusParams) => void;
    // cancelFocusRouteDrive: () => void;
    // driveOnRoute: (params: IDriveRouteParams) => Promise<IResult>;
    // //加载模型
    // load3Dtiles: (params: IModelParams) => Promise<IResult>;
    // remove3Dtiles: (params: IModelRemoveParams) => void;
    // loadGLTF: (params: IModelParams) => Promise<IResult>;
    // removeGLTF: (params: IModelRemoveParams) => void;
}
